import { useEffect, useState, useRef } from 'react'
import { DocumentArrowDownIcon, PlusIcon, PencilSquareIcon, TrashIcon, PrinterIcon, DocumentArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import TableComponent from 'components/common/TableComponent'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import ButtonComponent from 'components/common/ButtonComponent'
import StickersModal from 'components/modals/StickersModal'
import ImportStickersModal from 'components/modals/ImportStickersModal'
import PrintStickerModal from 'components/modals/PrintStickerModal'
import ActionCardComponent from 'components/common/ActionCardComponent'
import { stickerTypes, reusePolicies } from 'constants/stickers'
import { removeEmpty } from 'utils/objectUtils'
import { showAlert } from 'services/alerts.service'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const stickerExportColumns = [
  { key: 'stickerId', label: '#' },
  { key: 'workerName', label: 'Worker' },
  { key: 'containerName', label: 'Container' },
  { key: 'containerMaterial', label: 'Material' },
  { key: 'grossWeight', label: 'Wgt/Unit (kg)' },
  { key: 'reusePolicy', label: 'Reuse' },
  { key: 'resetEvery', label: 'Reset' },
  { key: 'resetTime', label: 'Reset Time' },
  { key: 'ranges', label: 'Ranges' },
  { key: 'quantity', label: 'Qty' },
  { key: 'stickerType', label: 'Type' },
  { key: 'secondsToClose', label: 'Close (s)' }
]

export default function Stickers () {
  const hasFetched = useRef(false)
  const { user } = useAuth()
  const { t } = useTranslation()

  const [state, setState] = useState({
    selectedSticker: null,
    stickers: [],
    containers: [],
    workers: [],
    isOpenStickersModal: false,
    isOpenImportModal: false,
    isOpenPrintModal: false,
    stickerToPrint: null,
    filter: '',
    refresh: 0,
    searchTerm: '',
    isImporting: false,
    loading: true,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const getStickers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/stickers/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      setState((s) => ({ ...s, stickers: response.data }))
    } catch (error) {
      setState((s) => ({ ...s, stickers: [] }))
    }
  }

  const getContainers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/management/containers',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })
      setState((s) => ({ ...s, containers: response.data }))
    } catch (error) {
      setState((s) => ({ ...s, containers: [] }))
    }
  }

  const getWorkers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/workers-management/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })
      setState((s) => ({ ...s, workers: response.data }))
    } catch (error) {
      setState((s) => ({ ...s, workers: [] }))
    }
  }

  const onSave = async (formData) => {
    formData = removeEmpty(formData)
    try {
      await makeRequest({
        method: 'POST',
        url: `/management/stickers/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        data: formData,
        alertResult: true
      })

      // refrech stickers to refresh the table
      await getStickers()

      toggleStickersModal()
    } catch (error) {
      setState((s) => ({ ...s, selectedSticker: formData }))
      showAlert({
        text: error.response?.data?.message || t('mgmt.stickers.errCreate'),
        icon: 'error'
      })
      toggleStickersModal()
    }
  }

  const handlePrintSticker = (sticker) => {
    setState(s => ({ ...s, isOpenPrintModal: true, stickerToPrint: sticker }))
  }

  const handlePrintDownload = async (range) => {
    const sticker = state.stickerToPrint
    try {
      const params = range ? `?from=${range.from}&to=${range.to}` : ''
      const response = await makeRequest({
        method: 'GET',
        url: `/management/stickers/${sticker.stickerId}/print${params}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        responseType: 'blob'
      })
      const blob = response.data
      let filename = `sticker_${sticker.stickerId}.pdf`
      const disposition = response.headers && response.headers['content-disposition']
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/)
        if (match && match[1]) filename = match[1]
      }
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      setState(s => ({ ...s, isOpenPrintModal: false, stickerToPrint: null }))
    } catch (error) {
      console.error('Failed to print sticker', error)
      showAlert({
        text: error.response?.data?.message || t('mgmt.stickers.errPrint'),
        icon: 'error'
      })
    }
  }

  const handleDelete = (sticker) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: sticker }))
  }

  const confirmDelete = async () => {
    const sticker = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/stickers/${sticker.stickerId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        alertResult: true
      })

      if (response.status === 200) {
        setState((s) => ({
          ...s,
          stickers: s.stickers.filter(item => item.stickerId !== sticker.stickerId)
        }))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleImportFromModal = async (file, isPacking) => {
    setState(s => ({ ...s, isImporting: true }))

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await makeRequest({
        method: 'POST',
        url: `/management/stickers/import/${user.farmId}?isPacking=${isPacking}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.status === 200) {
        showAlert({
          text: t('mgmt.stickers.importSuccess', { message: response.data.message, total: response.data.total }),
          icon: 'success'
        })

        // Refresh the stickers list
        await getStickers()

        // Close the modal
        toggleImportModal()
      }
    } catch (error) {
      console.error('Import failed:', error)
      showAlert({
        text: error.response?.data?.message || t('mgmt.stickers.errImport'),
        icon: 'error'
      })
    } finally {
      setState(s => ({ ...s, isImporting: false }))
    }
  }

  const toggleImportModal = () => {
    setState(s => ({ ...s, isOpenImportModal: !s.isOpenImportModal }))
  }

  const toggleStickersModal = (selectedSticker) => {
    setState(s => ({
      ...s,
      isOpenStickersModal: !state.isOpenStickersModal,
      selectedSticker
    }))
  }

  const filteredStickers = filterRowsBySearch(state.stickers, state.searchTerm)

  const stickersTableData = {
    columns: [
      { key: 'stickerId', label: '#' },
      { key: 'workerName', labelKey: 'mgmt.stickers.cols.worker' }, // Worker Name
      { key: 'containerName', label: 'Container' }, // Container Name
      { key: 'containerMaterial', label: 'Material' },
      { key: 'grossWeight', label: 'Wgt/Unit (kg)' }, // Weight per Unit (Kg)
      { key: 'reusePolicy', label: 'Reuse' },
      { key: 'resetEvery', label: 'Reset' }, // Reset Every
      { key: 'resetTime', label: 'Reset Time' }, // Daily Reset Time
      { key: 'ranges', label: 'Ranges' },
      { key: 'quantity', label: 'Qty' },
      { key: 'stickerType', label: 'Type' }, // Stickers Type
      { key: 'secondsToClose', label: 'Close (s)' }, // Seconds To Close
      {
        key: 'actions',
        labelKey: 'mgmt.stickers.cols.manage',
        cell: (value, sticker) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => handlePrintSticker(sticker)} title={t('common.print')} className='p-1 rounded transition'>
              <PrinterIcon className='w-5 h-5 text-green-600' />
            </button>
            <button onClick={() => toggleStickersModal(sticker)} title={t('common.edit')} className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(sticker)} title={t('common.delete')} className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: filteredStickers
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    Promise.all([
      getStickers(),
      getContainers(),
      getWorkers()
    ]).finally(() => {
      setState(s => ({ ...s, loading: false }))
    })
  }, [user?.farmId])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.stickers.loading')} rows={8} />
  }

  return (
    <>
      <ActionCardComponent>
        <input
          type='text'
          placeholder={t('mgmt.stickers.search')}
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />

        <ButtonComponent onClick={() => toggleStickersModal(null)}>
          <PlusIcon className='w-5 h-5 mr-1' />
          {t('mgmt.stickers.new')}
        </ButtonComponent>

        <ButtonComponent
          onClick={toggleImportModal}
          disabled={state.isImporting}
          className={state.isImporting ? 'opacity-50 cursor-not-allowed' : ''}
        >
          <DocumentArrowUpIcon className='w-5 h-5' />
          <span>{t('mgmt.stickers.importPdf')}</span>
        </ButtonComponent>

        <ButtonComponent onClick={() => exportDataEditExcel({
          rows: filteredStickers,
          columns: stickerExportColumns,
          fileName: `stickers-${new Date().toISOString().slice(0, 10)}`,
          sheetName: 'Stickers'
        })}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>{t('common.exportExcel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={() => exportDataEditPdf({
          rows: filteredStickers,
          columns: stickerExportColumns,
          fileName: `stickers-${new Date().toISOString().slice(0, 10)}`,
          title: 'Stickers'
        })}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>{t('mgmt.common.exportPdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>      {/* stickers table */}
      <TableComponent
        tableData={stickersTableData}
        defaultSortColumn={stickersTableData.columns[0]?.key}
      />

      {/* modal add/edit sticker */}
      <StickersModal
        isOpen={state.isOpenStickersModal}
        onClose={toggleStickersModal}
        onSave={onSave}
        selectedSticker={state.selectedSticker}
        stickerTypes={stickerTypes}
        reusePolicies={reusePolicies}
        containers={state.containers}
        workers={state.workers}
      />

      {/* modal import stickers */}
      <ImportStickersModal
        isOpen={state.isOpenImportModal}
        onClose={toggleImportModal}
        onImport={handleImportFromModal}
        isImporting={state.isImporting}
      />

      {/* modal print stickers — conditionally rendered so state resets on every open */}
      {state.isOpenPrintModal && (
        <PrintStickerModal
          isOpen
          onClose={() => setState(s => ({ ...s, isOpenPrintModal: false, stickerToPrint: null }))}
          sticker={state.stickerToPrint}
          onPrint={handlePrintDownload}
        />
      )}

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.stickers.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {t('mgmt.stickers.removeConfirm', { name: state.pendingDelete?.workerName || state.pendingDelete?.containerName || `#${state.pendingDelete?.stickerId}` })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}
